import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { enqueueSnackbar } from 'notistack';

import templateService, { Template, TemplateCreateData, TemplateUpdateData } from '../../services/templateService';
import SysEditor from '../syseditor/SysEditor';

// Schema de validação
const templateContratoSchema = yup.object().shape({
  title: yup
    .string()
    .required('Título é obrigatório')
    .min(1, 'Título deve ter pelo menos 1 caractere')
    .max(255, 'Título deve ter no máximo 255 caracteres'),
  content: yup
    .string()
    .required('Conteúdo do contrato é obrigatório')
    .min(1, 'Conteúdo não pode estar vazio'),
  termo_adesao: yup.string().nullable().default(''),
  rescisao_contrato: yup.string().nullable().default(''),
  isActive: yup.boolean().default(true),
}) as yup.ObjectSchema<FormData>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contrato-tabpanel-${index}`}
      aria-labelledby={`contrato-tab-${index}`}
      style={{
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

interface TemplateContratoFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  template?: Template | null;
}

interface FormData {
  title: string;
  content: string;
  termo_adesao: string;
  rescisao_contrato: string;
  isActive: boolean;
}

const TemplateContratoForm: React.FC<TemplateContratoFormProps> = ({
  open,
  onClose,
  onSuccess,
  template,
}) => {
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  const contratoTabs = [
    { label: 'Contrato', icon: <DescriptionIcon />, color: theme.palette.primary.main },
    { label: 'Termo de Adesão', icon: <AssignmentIcon />, color: '#ed6c02' },
    { label: 'Rescisão', icon: <CancelIcon />, color: theme.palette.error.main },
  ];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(templateContratoSchema) as any,
    defaultValues: {
      title: '',
      content: '',
      termo_adesao: '',
      rescisao_contrato: '',
      isActive: true,
    },
  });

  useEffect(() => {
    if (template) {
      reset({
        title: template.title,
        content: template.content,
        termo_adesao: template.termo_adesao ?? '',
        rescisao_contrato: template.rescisao_contrato ?? '',
        isActive: template.isActive,
      });
    } else {
      reset({
        title: '',
        content: '',
        termo_adesao: '',
        rescisao_contrato: '',
        isActive: true,
      });
    }
  }, [template, reset]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      if (template) {
        const updateData: TemplateUpdateData = {
          title: data.title,
          content: data.content,
          type: 'contrato',
          termo_adesao: data.termo_adesao?.trim() || null,
          rescisao_contrato: data.rescisao_contrato?.trim() || null,
          isActive: data.isActive,
        };
        await templateService.updateTemplate(template.id, updateData);
        enqueueSnackbar('Template de contrato atualizado com sucesso!', { variant: 'success' });
      } else {
        const createData: TemplateCreateData = {
          title: data.title,
          content: data.content,
          type: 'contrato',
          termo_adesao: data.termo_adesao?.trim() || null,
          rescisao_contrato: data.rescisao_contrato?.trim() || null,
          isActive: data.isActive,
        };
        await templateService.createTemplate(createData);
        enqueueSnackbar('Template de contrato criado com sucesso!', { variant: 'success' });
      }
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar template de contrato:', error);
      const message = error.response?.data?.error || 'Erro ao salvar template';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      setTabValue(0);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' },
      }}
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          {template ? 'Editar Template de Contrato' : 'Novo Template de Contrato'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box component="form" noValidate sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Grid container spacing={1.5} sx={{ px: 1.5, pt: 1 }}>
            <Grid item xs={12} md={8}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Título"
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={loading}
                        size="small"
                      />
                    }
                    label="Template Ativo"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                minHeight: 42,
                gap: 0.8,
              },
            }}
          >
            {contratoTabs.map((tab, idx) => (
              <Tab
                key={idx}
                icon={React.cloneElement(tab.icon, {
                  sx: { fontSize: 17, color: tabValue === idx ? tab.color : 'text.secondary' },
                })}
                iconPosition="start"
                label={tab.label}
                id={`contrato-tab-${idx}`}
                aria-controls={`contrato-tabpanel-${idx}`}
              />
            ))}
          </Tabs>

          <Box sx={{ px: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <SysEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Digite o conteúdo do contrato aqui..."
                      disabled={loading}
                      showVariables={true}
                      variablesType="contrato"
                    />
                  )}
                />
              </Box>
              {errors.content && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.content.message}
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Controller
                  name="termo_adesao"
                  control={control}
                  render={({ field }) => (
                    <SysEditor
                      value={field.value || ''}
                      onChange={(v) => field.onChange(v || '')}
                      placeholder="Digite o conteúdo do termo de adesão aqui..."
                      disabled={loading}
                      showVariables={true}
                      variablesType="contrato"
                    />
                  )}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Controller
                  name="rescisao_contrato"
                  control={control}
                  render={({ field }) => (
                    <SysEditor
                      value={field.value || ''}
                      onChange={(v) => field.onChange(v || '')}
                      placeholder="Digite o conteúdo da rescisão de contrato aqui..."
                      disabled={loading}
                      showVariables={true}
                      variablesType="contrato"
                    />
                  )}
                />
              </Box>
            </TabPanel>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Salvando...' : template ? 'Atualizar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateContratoForm;
